import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";

import SubscriptionPlan from "../SubscriptionPlan/SubscriptionPlan";
import SubscriptionPlans from "../SubscriptionPlan/SubscriptionList";
import CouponList from "../Coupon/CouponList";
import JewellerSubscription from "../SubscriptionPlan/JewellerSubscription";
import InfoButton from "../../Comman/InfoButton";

import { canShowPermission } from "../Permissions/permissionObject";

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const [permissions, setPermissions] = useState({});

  /* -------------------- Load Permissions -------------------- */
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      setPermissions(JSON.parse(storedPermissions));
    }
  }, []);

  /* -------------------- Permission Flags (VIEW like Offers) -------------------- */
  const canViewSubscriptionPlans = canShowPermission(
    permissions,
    "subscriptionMain",
    "view"
  );

  const canViewSubscriptions = canShowPermission(
    permissions,
    "subscriptionList",
    "view"
  );

  const canViewCoupons = canShowPermission(
    permissions,
    "couponlist",
    "view"
  );

  const canViewJewellerSubscriptions = canShowPermission(
    permissions,
    "jewellerSubscriptionList",
    "view"
  );

  /* -------------------- Tabs Config (Same as Offers) -------------------- */
  const tabs = useMemo(
    () =>
      [
        canViewSubscriptionPlans && {
          label: t("tabs.subscriptionPlans"),
          component: <SubscriptionPlan />,
        },
        canViewSubscriptions && {
          label: t("tabs.subscriptions"),
          component: <SubscriptionPlans />,
        },
        canViewCoupons && {
          label: t("tabs.coupons"),
          component: <CouponList />,
        },
        canViewJewellerSubscriptions && {
          label: t("tabs.jewellerSubscriptions"),
          component: <JewellerSubscription />,
        },
      ].filter(Boolean),
    [
      t,
      canViewSubscriptionPlans,
      canViewSubscriptions,
      canViewCoupons,
      canViewJewellerSubscriptions,
    ]
  );

  /* -------------------- Prevent Invalid Tab Index -------------------- */
  useEffect(() => {
    if (tabIndex >= tabs.length) {
      setTabIndex(0);
    }
  }, [tabs.length, tabIndex]);

  /* -------------------- Render -------------------- */
  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          width: "100%",
          mt: { xs: 10, md: 10 },
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Paper
          sx={{ p: { xs: 2, sm: 4 }, mb: 2, borderRadius: "16px" }}
          elevation={1}
        >
          {/* -------------------- Tabs Header -------------------- */}
          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "4px",
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                minWidth: "auto",
                px: 2,
                borderRadius: "6px",
                margin: "0 4px",
                minHeight: "48px",
                "&.Mui-selected": {
                  backgroundColor: "#fff",
                  color: "#7A3FFF",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  borderBottom: "3px solid #7A3FFF",
                },
                "&:hover": {
                  backgroundColor: "rgba(122, 63, 255, 0.08)",
                },
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>

          {/* -------------------- Tab Content (Same as Offers) -------------------- */}
          <Box sx={{ mt: 0 }}>
            {tabs[tabIndex]?.component}
          </Box>
        </Paper>
      </Box>
      <Box
    sx={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 1000,
    }}
  >
    <InfoButton pageName="SUBSCRIPTION" />
  </Box>
      </Box>
    </Box>
  );
}
